import { useCallback, useMemo } from "react";
import type {
  AdditionalDetailsData,
  AdyenCheckoutError,
} from "@adyen/adyen-web";
import { PaymentAuthAction } from "@b2bportal/purchase-api";
import { useCheckoutState, useCheckoutStateSelector } from "@checkout/context";
import {
  type CartFulfillEvents,
  CartFulfillEventType,
  CartFulfillState,
  getChildState,
} from "@checkout/index";
import { getCartFulfillPendingInteractive } from "@checkout/states/Cart/CartFulfill/selectors";
import { logger } from "@hopper-b2b/datadog";

export const useCartFulfill3ds = <MachineContext>() => {
  const [state, send] = useCheckoutState<CartFulfillEvents, MachineContext>();

  const childState = getChildState(state.value);

  const challengeData = useCheckoutStateSelector(
    getCartFulfillPendingInteractive
  );
  const challengeAction = useMemo(() => {
    if (childState !== CartFulfillState.challenge) {
      return;
    }
    if (challengeData == null) {
      return;
    }
    const action = challengeData.actions.find(
      (a) => a.type === PaymentAuthAction.InteractivePayment3DSAdyenAuthAction
    );
    if (action == null) {
      return;
    }
    return action;
  }, [childState, challengeData]);

  const should3DSDialogOpen = useMemo(
    () =>
      challengeAction?.type ===
      PaymentAuthAction.InteractivePayment3DSAdyenAuthAction,
    [challengeAction]
  );

  const on3DSDialogOpenChange = useCallback(
    (open: boolean) => {
      logger().error("on3DSDialogOpenChange: dialog closed abruptly");
      // TODO this should perhaps be used
      send(CartFulfillEventType.COMPLETE_INTERACTIVE_ERROR);
    },
    [send]
  );

  const on3dsSuccess = useCallback(
    (state: AdditionalDetailsData) => {
      if (challengeAction == null) {
        return;
      }
      const newValue = {
        ...challengeAction.value,
        details: state.data.details,
      };

      send({
        type: CartFulfillEventType.COMPLETE_INTERACTIVE_SUCCESS,
        data: {
          type: PaymentAuthAction.InteractivePayment3DSAdyenAuthAction,
          value: newValue,
        },
      });
    },
    [send, challengeAction]
  );

  const on3dsError = useCallback(
    (error: AdyenCheckoutError) => {
      logger().error("AdyenCheckoutError", { error });
      send(CartFulfillEventType.COMPLETE_INTERACTIVE_ERROR);
    },
    [send]
  );

  return useMemo(
    () => ({
      paymentAction: challengeAction?.value.action,
      should3DSDialogOpen,
      on3DSDialogOpenChange,
      on3dsSuccess,
      on3dsError,
    }),
    [
      challengeAction,
      should3DSDialogOpen,
      on3DSDialogOpenChange,
      on3dsSuccess,
      on3dsError,
    ]
  );
};
